import { Router } from '@angular/router';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  firstValueFrom,
  interval,
  takeWhile,
} from 'rxjs';
import {
  CrawlerDialogComponent,
} from 'src/app/dialogs/crawler-dialog/crawler-dialog.component';
import {
  Crawler,
  CrawlerType,
  IntegrationWithCrawler,
  ACTIVE_CRAWLER_STATUSES,
} from 'src/app/models/integration/crawler';
import { Paginator } from 'src/app/models/paginator';
import { IntegrationService } from 'src/app/services/integration.service';
import { MessageService } from 'src/app/services/message.service';
import { AclService } from 'src/app/services/acl.service';
import {
  CrawlerFilterValues,
} from 'src/app/components/crawler-filters-drawer/crawler-filters-drawer.component';

@Component({
  selector: 'app-crawler-page',
  templateUrl: './crawler-page.component.html',
  styleUrls: ['./crawler-page.component.scss']
})
export class CrawlerPageComponent implements OnInit {

  loading = false;
  done = false;
  noDocs = false;
  autoRefresh = false;

  crawlers: Paginator<IntegrationWithCrawler> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-integration.createdAt'
  };

  displayedColumns: string[] = ['name', 'type', 'status', 'progress', 'recurrence', 'trademark', 'countries', 'owner'];

  // Latest filter values emitted by app-crawler-filters-drawer.
  activeFilters: CrawlerFilterValues = {
    status: [],
    recurrence: [],
    type: [],
    trademark: [],
    countries: [],
    query: '',
  };

  noIntegrationButton = {
    label: 'INTEGRATIONS.CREATE_TITLE',
    icon: 'power',
    class: 'main-button',
    action: () => { this.addIntegrationDialog().then(); }
  };

  // Exposed for the template.
  protected readonly CrawlerType = CrawlerType;

  constructor(
    private readonly _message: MessageService,
    private readonly dialog: MatDialog,
    private readonly integrationService: IntegrationService,
    private readonly toastr: ToastrService,
    private readonly aclService: AclService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
  ) {}

  async ngOnInit() {
    this.loading = true;
    this.done = false;
    await this.retrieveCrawlers(true);
    this.loading = false;
    this.done = true;

    this.checkAndEnableAutoRefresh();
  }

  // ── Auto-refresh ───────────────────────────────────────────────────────────

  checkAndEnableAutoRefresh() {
    const hasActive = this.crawlers.docs.some(row =>
      ACTIVE_CRAWLER_STATUSES.has(row.crawler.progress?.status)
    );

    if (hasActive && !this.autoRefresh) {
      this.enableAutoRefresh();
    } else if (!hasActive && this.autoRefresh) {
      this.disableAutoRefresh();
    }
  }

  enableAutoRefresh() {
    this.autoRefresh = true;
    interval(10000)
      .pipe(
        takeWhile(() => this.autoRefresh),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => { this.refreshCrawlers().then(); });
  }

  disableAutoRefresh() {
    this.autoRefresh = false;
  }

  async refreshCrawlers() {
    await this.retrieveCrawlers(false);
    this.checkAndEnableAutoRefresh();
  }

  // ── Data fetching ──────────────────────────────────────────────────────────

  async retrieveCrawlers(init: boolean) {
    try {
      if (!this.autoRefresh) {
        this._message.emitChange('LOADING', 'START');
      }

      const f = this.activeFilters;
      this.crawlers = {
        ...this.crawlers,
        ...await this.integrationService.retrieveAll(
          this.crawlers.page,
          this.crawlers.limit,
          this.crawlers.offset,
          this.crawlers.sort,
          f.type,
          f.recurrence,
          f.trademark,
          f.query,
          f.status,
          f.countries
        )
      };

      if (init) {
        this.noDocs = this.crawlers.totalDocs === 0;
      }

      if (!this.autoRefresh) {
        this._message.emitChange('LOADING', 'END');
      }
    } catch (err) {
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  resetPage() {
    this.crawlers.limit = 10;
    this.crawlers.page = 1;
    this.crawlers.offset = 0;
  }

  async handlePage(event: PageEvent) {
    this.crawlers.limit = event.pageSize;
    this.crawlers.page = event.pageIndex + 1;
    this.crawlers.offset = event.pageIndex * this.crawlers.limit;
    await this.retrieveCrawlers(false);
  }

  onSortChange(event: { active: string; direction: 'asc' | 'desc' | '' }) {
    if (event.direction !== 'asc' && event.direction !== 'desc') return;
    this.crawlers.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
    this.retrieveCrawlers(false);
  }

  onFiltersChange(filters: CrawlerFilterValues) {
    this.activeFilters = filters;
    this.resetPage();
    this.retrieveCrawlers(false);
  }

  // ── Per-type predicates / helpers ──────────────────────────────────────────

  hasSpecUrl(crawler: Crawler): boolean {
    return crawler.type === CrawlerType.SINGLE_URL || crawler.type === CrawlerType.DISCOVERY;
  }

  getSpecUrl(crawler: Crawler): string | undefined {
    if (crawler.type === CrawlerType.SINGLE_URL || crawler.type === CrawlerType.DISCOVERY) {
      return crawler.spec.url;
    }
    return undefined;
  }

  getProgressPercentage(crawler: Crawler): number {
    if (!crawler.progress || crawler.progress.total === 0) return 0;
    return Math.round((crawler.progress.processed / crawler.progress.total) * 100);
  }

  nextTrigger(crawler: Crawler): string {
    if (crawler.recurrence === 'week') {
      return moment().add(1, 'week').startOf('week').format('ll');
    }
    return moment().add(1, 'month').startOf('month').format('ll');
  }

  onceTrigger(row: IntegrationWithCrawler): string {
    return moment(row.integration.createdAt).format('ll');
  }

  // Navigation gate for the row's name link: per-type access mirrors the
  // single-crawler page trigger rights (#467 admins+/#512 managers+).
  canTriggerIntegrationOrIsSuperAdmin(integration: IntegrationWithCrawler): boolean {
    const { type } = integration.crawler;
    if (type === CrawlerType.SINGLE_URL) return true;
    if (type === CrawlerType.EXCEL_BATCH) {
      return this.aclService.isAdminOrSuperAdmin();
    }
    // Discovery: Managers and above, the team inside a client organisation by the role it acts with.
    return this.aclService.isManagerOrAbove();
  }

  // ── dialogs ───────────────────────────────────────────────────

  async addIntegrationDialog() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '880px',
      maxWidth: '92dvw',
      maxHeight: '90dvh',
      autoFocus: false,
      data: { mode: 'create' }
    };
    const dialog = this.dialog.open(CrawlerDialogComponent, config);
    const result: { created: boolean; edited: boolean; integrationId?: string } = await firstValueFrom(dialog.afterClosed());
    // Straight to the new integration's page, where its run (if launched) can be followed.
    if (result?.created && result.integrationId) {
      await this.router.navigate(['integrations', 'website', result.integrationId]);
      return;
    }
    if (result?.created) {
      this.resetPage();
      await this.retrieveCrawlers(true);
      this.checkAndEnableAutoRefresh();
    }
  }
}
